// src/components/Analytics/TimeAnalytics.jsx
import React, { useEffect, useMemo, useState } from "react";
import { db } from "../../firebase/config";
import { collection, getDoc, doc, getDocs } from "firebase/firestore";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import { DateTime } from "luxon";
import "../../styles/Analytics.css";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

// --- Date range helpers (Luxon, TZ-safe) ---
const formatMDY = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${m}/${d}/${y}`;
};

function buildRangeFromPreset(preset, tz) {
  const end = DateTime.now()
    .setZone(tz)
    .startOf("day");
  let start = end;

  switch (preset) {
    case "LAST_7":
      start = end.minus({ days: 6 });
      break;
    case "LAST_30":
      start = end.minus({ days: 29 });
      break;
    case "LAST_90":
      start = end.minus({ days: 89 });
      break;
    case "MTD":
      start = end.startOf("month");
      break;
    case "YTD":
      start = end.startOf("year");
      break;
    default:
      start = end.minus({ days: 29 });
  }

  return { startISO: start.toISODate(), endISO: end.toISODate() };
}

function entryISODateInTZ(entry, tz) {
  // Prefer explicit date field if it exists (you already write e.date in some cases)
  if (entry?.date && typeof entry.date === "string") return entry.date;

  // Otherwise derive from clockIn in company TZ
  if (entry?.clockIn) {
    const dt = DateTime.fromISO(entry.clockIn, { zone: tz });
    return dt.isValid ? dt.toISODate() : null;
  }

  // As a last resort, try createdAt timestamp (if you add it later)
  const createdAt = entry?.createdAt;
  if (createdAt && typeof createdAt?.toDate === "function") {
    const dt = DateTime.fromJSDate(createdAt.toDate(), { zone: tz });
    return dt.isValid ? dt.toISODate() : null;
  }

  return null;
}

export default function TimeAnalytics({ companyId, employeeId = null }) {
  // ───────────────────────────────── state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [companyTimeZone, setCompanyTimeZone] = useState("UTC");

  const [timeEntries, setTimeEntries] = useState([]);
  const [employees, setEmployees] = useState({}); // { employeeDocId: "Name" }

  const isDrillDown = !!employeeId;

  // ✅ Date range state
  const [rangePreset, setRangePreset] = useState("LAST_30");
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");

  // ───────────────────────────────── guards
  useEffect(() => {
    if (!companyId) {
      setError("Company not loaded.");
      setLoading(false);
    } else {
      setError("");
    }
  }, [companyId]);

  // ───────────────────────────────── company timezone
  useEffect(() => {
    if (!companyId) return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "companies", companyId));
        setCompanyTimeZone(snap.data()?.timeZone || "UTC");
      } catch {
        setCompanyTimeZone("UTC");
      }
    })();
  }, [companyId]);

  useEffect(() => {
    if (!companyTimeZone) return;

    if (rangePreset === "CUSTOM") {
      const todayISO = DateTime.now()
        .setZone(companyTimeZone)
        .toISODate();

      // only fill blanks, don’t overwrite user input
      if (!rangeStart) setRangeStart(todayISO);
      if (!rangeEnd) setRangeEnd(todayISO);
      return;
    }

    const { startISO, endISO } = buildRangeFromPreset(
      rangePreset,
      companyTimeZone
    );

    // only update if different (prevents re-render loops)
    if (rangeStart !== startISO) setRangeStart(startISO);
    if (rangeEnd !== endISO) setRangeEnd(endISO);
  }, [rangePreset, companyTimeZone, rangeStart, rangeEnd]); // ✅ do NOT include rangeStart/rangeEnd here

  // ✅ Guard: keep start <= end
  useEffect(() => {
    if (!rangeStart || !rangeEnd) return;
    const s = DateTime.fromISO(rangeStart, { zone: companyTimeZone });
    const e = DateTime.fromISO(rangeEnd, { zone: companyTimeZone });
    if (s.isValid && e.isValid && s > e) {
      setRangeEnd(rangeStart);
    }
  }, [rangeStart, rangeEnd, companyTimeZone]);

  // ───────────────────────────────── employees map (for name fallback)
  useEffect(() => {
    if (!companyId) return;
    (async () => {
      try {
        const col = collection(db, `companies/${companyId}/employees`);
        const snap = await getDocs(col);
        const map = {};
        snap.forEach((d) => {
          const e = d.data() || {};
          const label =
            `${e.name || ""} ${e.lastName || ""}`.trim() ||
            e.email ||
            "Unknown";
          map[d.id] = label;
        });
        setEmployees(map);
      } catch (e) {
        console.warn("Employees load error:", e?.message);
        setEmployees({});
      }
    })();
  }, [companyId]);

  // ───────────────────────────────── time entries
  useEffect(() => {
    if (!companyId) return;

    (async () => {
      try {
        setLoading(true);
        setError("");

        const snap = await getDocs(
          collection(db, `companies/${companyId}/time_tracking`)
        );

        const rows = snap.docs.map((d) => {
          const raw = d.data() || {};
          const idFromDoc = raw.userId || raw.employeeId || null;

          return {
            id: d.id,
            ...raw,
            _employeeId: idFromDoc,
            employeeName:
              raw.employeeName ||
              (idFromDoc ? employees[idFromDoc] : null) ||
              "Unknown",
          };
        });

        setTimeEntries(rows);
      } catch (err) {
        console.error(err);
        setError("Failed to fetch time entries.");
      } finally {
        setLoading(false);
      }
    })();
  }, [companyId, employees]);

  // ✅ DRILL-DOWN: filter to selected employee (if provided)
  const employeeScoped = useMemo(() => {
    if (!employeeId) return timeEntries;
    return timeEntries.filter((e) => (e._employeeId || "") === employeeId);
  }, [timeEntries, employeeId]);

  // ✅ Date range filter (company TZ)
  const scopedEntries = useMemo(() => {
    if (!rangeStart || !rangeEnd) return employeeScoped;

    const start = DateTime.fromISO(rangeStart, {
      zone: companyTimeZone,
    }).startOf("day");
    const end = DateTime.fromISO(rangeEnd, { zone: companyTimeZone }).startOf(
      "day"
    );

    if (!start.isValid || !end.isValid) return employeeScoped;

    // inclusive range: [start, end]
    const startMs = start.toMillis();
    const endMs = end.toMillis();

    return employeeScoped.filter((entry) => {
      const iso = entryISODateInTZ(entry, companyTimeZone);
      if (!iso) return false;
      const d = DateTime.fromISO(iso, { zone: companyTimeZone }).startOf("day");
      if (!d.isValid) return false;
      const ms = d.toMillis();
      return ms >= startMs && ms <= endMs;
    });
  }, [employeeScoped, rangeStart, rangeEnd, companyTimeZone]);

  // ───────────────────────────────── metrics (use scopedEntries)
  const totalEntries = scopedEntries.length;

  const totalHours = scopedEntries.reduce(
    (sum, e) => sum + Number(e.hoursWorked || 0),
    0
  );
  const avgHours = totalEntries ? (totalHours / totalEntries).toFixed(2) : 0;

  const lateCount = scopedEntries.filter((e) =>
    (e.status || "").toLowerCase().includes("late")
  ).length;
  const latePercentage = totalEntries
    ? ((lateCount / totalEntries) * 100).toFixed(1)
    : 0;

  const earlyCount = scopedEntries.filter((e) =>
    (e.status || "").toLowerCase().includes("early")
  ).length;
  const earlyPercentage = totalEntries
    ? ((earlyCount / totalEntries) * 100).toFixed(1)
    : 0;

  const onTimeCount = scopedEntries.filter(
    (e) => (e.status || "").toLowerCase() === "on time"
  ).length;
  const onTimeRate = totalEntries
    ? ((onTimeCount / totalEntries) * 100).toFixed(1)
    : 0;

  const forcedCount = scopedEntries.filter((e) => !!e.forcedClockOut).length;
  const editedCount = scopedEntries.filter((e) => !!e.wasEdited).length;

  // Avg clock-in time (company TZ)
  const clockInMinutes = scopedEntries
    .filter((e) => !!e.clockIn)
    .map((e) => {
      const dt = DateTime.fromISO(e.clockIn, { zone: companyTimeZone });
      return dt.isValid ? dt.hour * 60 + dt.minute : null;
    })
    .filter((n) => Number.isFinite(n));

  const avgClockInMin =
    clockInMinutes.length > 0
      ? Math.round(
          clockInMinutes.reduce((a, b) => a + b, 0) / clockInMinutes.length
        )
      : null;

  const avgClockInLabel =
    avgClockInMin == null
      ? "—"
      : DateTime.fromObject(
          { hour: Math.floor(avgClockInMin / 60), minute: avgClockInMin % 60 },
          { zone: companyTimeZone }
        ).toFormat("h:mm a");

  // Hour-of-day activity (respect company TZ)
  const clockHourCounts = scopedEntries.reduce((acc, e) => {
    if (!e.clockIn) return acc;
    const h = DateTime.fromISO(e.clockIn, { zone: companyTimeZone }).hour;
    acc[h] = (acc[h] || 0) + 1;
    return acc;
  }, {});
  const hours0to23 = [...Array(24).keys()];
  const activeHourData = hours0to23.map((h) => clockHourCounts[h] || 0);

  // ───────────────────────────────── leaderboards (ONLY when not drilling down)
  const topHours = useMemo(() => {
    if (isDrillDown) return [];
    const hoursByEmp = {};
    scopedEntries.forEach((e) => {
      const name = e.employeeName || "Unknown";
      hoursByEmp[name] = (hoursByEmp[name] || 0) + Number(e.hoursWorked || 0);
    });
    return Object.entries(hoursByEmp)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  }, [scopedEntries, isDrillDown]);

  const topLate = useMemo(() => {
    if (isDrillDown) return [];
    const lateByEmp = {};
    scopedEntries.forEach((e) => {
      if ((e.status || "").toLowerCase().includes("late")) {
        const name = e.employeeName || "Unknown";
        lateByEmp[name] = (lateByEmp[name] || 0) + 1;
      }
    });
    return Object.entries(lateByEmp)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  }, [scopedEntries, isDrillDown]);

  const topEarly = useMemo(() => {
    if (isDrillDown) return [];
    const earlyByEmp = {};
    scopedEntries.forEach((e) => {
      if ((e.status || "").toLowerCase().includes("early")) {
        const name = e.employeeName || "Unknown";
        earlyByEmp[name] = (earlyByEmp[name] || 0) + 1;
      }
    });
    return Object.entries(earlyByEmp)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  }, [scopedEntries, isDrillDown]);

  const minSessions = 5;
  const bestOnTime = useMemo(() => {
    if (isDrillDown) return [];
    const statsByEmp = {};
    scopedEntries.forEach((e) => {
      const name = e.employeeName || "Unknown";
      if (!statsByEmp[name]) statsByEmp[name] = { tot: 0, on: 0 };
      statsByEmp[name].tot += 1;
      if ((e.status || "").toLowerCase() === "on time")
        statsByEmp[name].on += 1;
    });

    return Object.entries(statsByEmp)
      .filter(([, v]) => v.tot >= minSessions)
      .map(([name, v]) => [name, (v.on / v.tot) * 100])
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  }, [scopedEntries, isDrillDown]);

  // ───────────────────────────────── drill-down charts (ONLY when drilling down)
  const dailyHoursInRange = useMemo(() => {
    if (!isDrillDown) return { labels: [], data: [] };
    if (!rangeStart || !rangeEnd) return { labels: [], data: [] };

    // group by date (already in TZ due to filter logic)
    const byDate = {};
    scopedEntries.forEach((e) => {
      const dayKey = entryISODateInTZ(e, companyTimeZone);
      if (!dayKey) return;
      byDate[dayKey] = (byDate[dayKey] || 0) + Number(e.hoursWorked || 0);
    });

    const start = DateTime.fromISO(rangeStart, {
      zone: companyTimeZone,
    }).startOf("day");
    const end = DateTime.fromISO(rangeEnd, { zone: companyTimeZone }).startOf(
      "day"
    );
    if (!start.isValid || !end.isValid) return { labels: [], data: [] };

    const days = [];
    for (let d = start; d <= end; d = d.plus({ days: 1 })) {
      days.push(d.toISODate());
    }

    return {
      labels: days.map((d) =>
        DateTime.fromISO(d, { zone: companyTimeZone }).toFormat("MMM d")
      ),
      data: days.map((d) => Number((byDate[d] || 0).toFixed(2))),
    };
  }, [scopedEntries, isDrillDown, companyTimeZone, rangeStart, rangeEnd]);

  const statusBreakdown = useMemo(() => {
    if (!isDrillDown) return { labels: [], data: [] };

    const counts = { "On Time": 0, Late: 0, Early: 0, Other: 0 };

    scopedEntries.forEach((e) => {
      const s = (e.status || "").toLowerCase();
      if (s === "on time") counts["On Time"] += 1;
      else if (s.includes("late")) counts["Late"] += 1;
      else if (s.includes("early")) counts["Early"] += 1;
      else counts["Other"] += 1;
    });

    const labels = Object.keys(counts).filter((k) => counts[k] > 0);
    return { labels, data: labels.map((k) => counts[k]) };
  }, [scopedEntries, isDrillDown]);

  // ───────────────────────────────── charts
  const barOptions = {
    responsive: true,
    plugins: { legend: { position: "top" } },
    scales: { y: { beginAtZero: true } },
  };

  const activeHoursChart = {
    labels: hours0to23.map((h) => `${h}:00`),
    datasets: [
      {
        label: `Clock-ins (${formatMDY(rangeStart)} → ${formatMDY(rangeEnd)})`,
        data: activeHourData,
        backgroundColor: (ctx) => {
          const { chartArea, ctx: g } = ctx.chart;
          if (!chartArea) return "#36A2EB";
          const grad = g.createLinearGradient(
            0,
            chartArea.top,
            0,
            chartArea.bottom
          );
          grad.addColorStop(0, "#2F5C71");
          grad.addColorStop(1, "#8EEDF7");
          return grad;
        },
        borderRadius: 6,
        borderSkipped: false,
      },
    ],
  };

  // Leaderboard charts (general mode only)
  const topHoursChart = {
    labels: topHours.map(([name]) => name),
    datasets: [
      {
        label: `Total Hours Worked (${formatMDY(rangeStart)} → ${formatMDY(
          rangeEnd
        )})`,
        data: topHours.map(([, hours]) => Number(hours.toFixed(2))),
        backgroundColor: (ctx) => {
          const { chartArea, ctx: g } = ctx.chart;
          if (!chartArea) return "#4CAF50";
          const grad = g.createLinearGradient(
            0,
            chartArea.top,
            0,
            chartArea.bottom
          );
          grad.addColorStop(0, "#2DB882");
          grad.addColorStop(1, "#FFF650");
          return grad;
        },
        borderRadius: 6,
        borderSkipped: false,
      },
    ],
  };

  const topLateChart = {
    labels: topLate.map(([name]) => name),
    datasets: [
      {
        label: `Late Clock-ins (${formatMDY(rangeStart)} → ${formatMDY(
          rangeEnd
        )})`,
        data: topLate.map(([, count]) => count),
        backgroundColor: (ctx) => {
          const { chartArea, ctx: g } = ctx.chart;
          if (!chartArea) return "#FF6B6B";
          const grad = g.createLinearGradient(
            0,
            chartArea.top,
            0,
            chartArea.bottom
          );
          grad.addColorStop(0, "rgba(255,107,107,0.95)");
          grad.addColorStop(1, "#465DF5");
          return grad;
        },
        borderRadius: 6,
        borderSkipped: false,
      },
    ],
  };

  const topEarlyChart = {
    labels: topEarly.map(([name]) => name),
    datasets: [
      {
        label: `Early Clock-ins (${formatMDY(rangeStart)} → ${formatMDY(
          rangeEnd
        )})`,
        data: topEarly.map(([, count]) => count),
        backgroundColor: (ctx) => {
          const { chartArea, ctx: g } = ctx.chart;
          if (!chartArea) return "#7B16B6";
          const grad = g.createLinearGradient(
            0,
            chartArea.top,
            0,
            chartArea.bottom
          );
          grad.addColorStop(0, "rgba(123,22,182,0.95)");
          grad.addColorStop(1, "#66B9F0");
          return grad;
        },
        borderRadius: 6,
        borderSkipped: false,
      },
    ],
  };

  const bestOnTimeChart = {
    labels: bestOnTime.map(([name]) => name),
    datasets: [
      {
        label: `On-time Rate (min ${minSessions})`,
        data: bestOnTime.map(([, pct]) => Number(pct.toFixed(1))),
        backgroundColor: (ctx) => {
          const { chartArea, ctx: g } = ctx.chart;
          if (!chartArea) return "#34D399";
          const grad = g.createLinearGradient(
            0,
            chartArea.top,
            0,
            chartArea.bottom
          );
          grad.addColorStop(0, "rgba(52,211,153,0.95)");
          grad.addColorStop(1, "rgba(52,211,153,0.55)");
          return grad;
        },
        borderRadius: 6,
        borderSkipped: false,
      },
    ],
  };

  // Drill-down charts (employee mode only)
  const dailyHoursChart = {
    labels: dailyHoursInRange.labels,
    datasets: [
      {
        label: `Hours Worked (${formatMDY(rangeStart)} → ${formatMDY(
          rangeEnd
        )})`,
        data: dailyHoursInRange.data,
        backgroundColor: (ctx) => {
          const { chartArea, ctx: g } = ctx.chart;
          if (!chartArea) return "#60A5FA";
          const grad = g.createLinearGradient(
            0,
            chartArea.top,
            0,
            chartArea.bottom
          );
          grad.addColorStop(0, "rgba(96,165,250,0.95)");
          grad.addColorStop(1, "rgba(96,165,250,0.45)");
          return grad;
        },
        borderRadius: 6,
        borderSkipped: false,
      },
    ],
  };

  const statusBreakdownChart = {
    labels: statusBreakdown.labels,
    datasets: [
      {
        label: `Sessions (${formatMDY(rangeStart)} → ${formatMDY(rangeEnd)})`,
        data: statusBreakdown.data,
        backgroundColor: (ctx) => {
          const { chartArea, ctx: g } = ctx.chart;
          if (!chartArea) return "#A78BFA";
          const grad = g.createLinearGradient(
            0,
            chartArea.top,
            0,
            chartArea.bottom
          );
          grad.addColorStop(0, "rgba(167,139,250,0.95)");
          grad.addColorStop(1, "rgba(167,139,250,0.45)");
          return grad;
        },
        borderRadius: 6,
        borderSkipped: false,
      },
    ],
  };

  // ───────────────────────────────── UI
  return (
    <div className="management-analytics-wrapper">
      {error && <p className="error-text">{error}</p>}

      {!companyId ? (
        <p>Loading...</p>
      ) : loading ? (
        <p>Loading...</p>
      ) : (
        <>
          {/* ✅ Date range controls */}
          <div className="analytics-rangebar">
            <div className="analytics-filter">
              <label className="analytics-filter-label">Date Range</label>
              <select
                className="analytics-dropdown"
                value={rangePreset}
                onChange={(e) => setRangePreset(e.target.value)}
              >
                <option value="LAST_7">Last 7 days</option>
                <option value="LAST_30">Last 30 days</option>
                <option value="LAST_90">Last 90 days</option>
                <option value="MTD">Month to date</option>
                <option value="YTD">Year to date</option>
                <option value="CUSTOM">Custom</option>
              </select>
            </div>

            <div className="analytics-filter">
              <label className="analytics-filter-label">Start</label>
              <input
                className="analytics-date-input"
                type="date"
                value={rangeStart}
                onChange={(e) => {
                  setRangePreset("CUSTOM");
                  setRangeStart(e.target.value);
                }}
              />
            </div>

            <div className="analytics-filter">
              <label className="analytics-filter-label">End</label>
              <input
                className="analytics-date-input"
                type="date"
                value={rangeEnd}
                onChange={(e) => {
                  setRangePreset("CUSTOM");
                  setRangeEnd(e.target.value);
                }}
              />
            </div>

            <div className="analytics-range-pill">
              {rangeStart && rangeEnd
                ? `${formatMDY(rangeStart)} → ${formatMDY(rangeEnd)}`
                : "—"}
            </div>
          </div>

          {/* Hint */}
          <div className="analytics-hint">
            {isDrillDown ? (
              <>
                Showing Time Tracking analytics for <b>1 employee</b>.
              </>
            ) : (
              <>
                Showing Time Tracking analytics for <b>all employees</b>.
              </>
            )}
            <span className="analytics-hint-sub">
              &nbsp;·&nbsp; TZ: <b>{companyTimeZone}</b>
            </span>
          </div>

          {/* KPI grid */}
          <div className="kpi-grid">
            {[
              {
                key: "onTime",
                label: "On-time Rate",
                value: `${onTimeRate}%`,
                accent: "accent-green",
              },
              {
                key: "early",
                label: "Early Clock-ins",
                value: `${earlyPercentage}%`,
                accent: "accent-purple",
              },
              {
                key: "late",
                label: "Late Clock-ins",
                value: `${latePercentage}%`,
                accent: "accent-red",
              },
              {
                key: "avgHr",
                label: "Avg Hours / Entry",
                value: `${avgHours} hrs`,
                accent: "accent-blue",
              },
              {
                key: "avgIn",
                label: "Avg Clock-in",
                value: `${avgClockInLabel}`,
                sub: `(${companyTimeZone})`,
                accent: "accent-teal",
              },
              {
                key: "forced",
                label: "Forced Clock-outs",
                value: `${forcedCount}`,
                accent: "accent-amber",
              },
              {
                key: "edited",
                label: "Edited Entries",
                value: `${editedCount}`,
                accent: "accent-indigo",
              },
              {
                key: "total",
                label: "Total Entries",
                value: `${totalEntries}`,
                accent: "accent-slate",
              },
            ].map(({ key, label, value, sub, accent }) => (
              <div className={`kpi-card ${accent}`} key={key}>
                <div className="kpi-top">
                  <span className="kpi-dot" />
                  <span className="kpi-label">{label}</span>
                </div>
                <div className="kpi-value">{value}</div>
                <div className="kpi-sub">{sub ?? "\u00A0"}</div>
              </div>
            ))}
          </div>

          {/* Charts */}
          <div className="charts-grid">
            <div className="chart-container">
              <h3>Most Active Clock-in Hours</h3>
              <Bar data={activeHoursChart} options={barOptions} />
            </div>

            {isDrillDown ? (
              <>
                <div className="chart-container">
                  <h3>Daily Hours Worked</h3>
                  <Bar data={dailyHoursChart} options={barOptions} />
                </div>

                <div className="chart-container">
                  <h3>Status Breakdown (On Time / Late / Early)</h3>
                  <Bar data={statusBreakdownChart} options={barOptions} />
                </div>
              </>
            ) : (
              <>
                <div className="chart-container">
                  <h3>Top 5 Employees by Total Hours</h3>
                  <Bar data={topHoursChart} options={barOptions} />
                </div>

                <div className="chart-container">
                  <h3>Top 5 Employees with Most Late Clock-ins</h3>
                  <Bar data={topLateChart} options={barOptions} />
                </div>

                <div className="chart-container">
                  <h3>Top 5 Employees with Most Early Clock-ins</h3>
                  <Bar data={topEarlyChart} options={barOptions} />
                </div>

                <div className="chart-container">
                  <h3>Best On-time Rate (min {minSessions} sessions)</h3>
                  <Bar data={bestOnTimeChart} options={barOptions} />
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
